import React, { useState, useEffect } from "react";
import Label from "../fields/Label";
import { MdClose } from "react-icons/md";
import pica from "pica";
import {
  Comment,
  CreateCommentInput,
  UpdateCommentInput,
  PostAttachment,
} from "../../types/community";
import Button from "../common/Button";
import ModalShell from "../modals/ModalShell";
import { logger } from "../../utils/logger";

const MAX_ATTACHMENTS = 5;
const MAX_ATTACHMENT_SIZE_BYTES = 10 * 1024 * 1024; // 10MB
const MAX_IMAGE_DIMENSION = 1920;
const ALLOWED_FILE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
];
const ALLOWED_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp"];

type PendingAttachment = {
  file: File;
  preview: string;
};

interface CommentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateCommentInput | UpdateCommentInput) => Promise<void>;
  comment?: Comment | null;
  loading?: boolean;
  /** Request error from the owning page; shown in the modal footer banner. */
  error?: string | null;
}

const CommentModal: React.FC<CommentModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  comment = null,
  loading = false,
  error = null,
}) => {
  const [content, setContent] = useState("");
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [existingAttachments, setExistingAttachments] = useState<
    PostAttachment[]
  >([]);
  const [attachmentsToRemove, setAttachmentsToRemove] = useState<number[]>([]);
  const [newAttachments, setNewAttachments] = useState<PendingAttachment[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const isEditMode = !!comment;
  const picaInstance = React.useMemo(() => pica(), []);

  const compressImageIfNeeded = async (file: File): Promise<File> => {
    if (
      !file.type.startsWith("image/") ||
      file.type === "image/gif" ||
      file.size <= MAX_ATTACHMENT_SIZE_BYTES
    ) {
      return file;
    }

    try {
      const imageBitmap = await createImageBitmap(file);
      const scale =
        imageBitmap.width > imageBitmap.height
          ? Math.min(1, MAX_IMAGE_DIMENSION / imageBitmap.width)
          : Math.min(1, MAX_IMAGE_DIMENSION / imageBitmap.height);

      const targetWidth = Math.max(1, Math.round(imageBitmap.width * scale));
      const targetHeight = Math.max(1, Math.round(imageBitmap.height * scale));

      const sourceCanvas = document.createElement("canvas");
      sourceCanvas.width = imageBitmap.width;
      sourceCanvas.height = imageBitmap.height;
      const sourceCtx = sourceCanvas.getContext("2d");
      sourceCtx?.drawImage(imageBitmap, 0, 0);

      const targetCanvas = document.createElement("canvas");
      targetCanvas.width = targetWidth;
      targetCanvas.height = targetHeight;

      await picaInstance.resize(sourceCanvas, targetCanvas, {
        unsharpAmount: 80,
        unsharpRadius: 0.6,
        unsharpThreshold: 2,
      });
      const blob = await picaInstance.toBlob(targetCanvas, "image/jpeg", 0.82);

      const compressedFile = new File(
        [blob],
        file.name.replace(/\.(png|jpg|jpeg|webp)$/i, "") + ".jpg",
        {
          type: "image/jpeg",
          lastModified: Date.now(),
        }
      );
      return compressedFile.size < file.size ? compressedFile : file;
    } catch (error) {
      logger.warn("Image compression failed, using original file:", error);
      return file;
    }
  };

  type FileValidationResult = {
    valid: boolean;
    error?: string;
  };

  const validateFile = (file: File): FileValidationResult => {
    const fileExtension = file.name.split(".").pop()?.toLowerCase();
    const isValidType =
      ALLOWED_FILE_TYPES.includes(file.type) ||
      (fileExtension && ALLOWED_EXTENSIONS.includes(fileExtension));

    if (!isValidType) {
      return {
        valid: false,
        error: `"${file.name}" — unsupported format. Allowed: JPG, PNG, GIF, WEBP`,
      };
    }

    // GIFs cannot be compressed (destroys animation) - reject if too large
    const isGif = file.type === "image/gif" || fileExtension === "gif";
    if (isGif && file.size > MAX_ATTACHMENT_SIZE_BYTES) {
      return {
        valid: false,
        error: `"${file.name}" — GIF too large (${(
          file.size /
          (1024 * 1024)
        ).toFixed(1)} MB). Max: 10 MB. GIFs cannot be compressed.`,
      };
    }

    return { valid: true };
  };

  useEffect(() => {
    if (isOpen) {
      if (isEditMode && comment) {
        setContent(comment.content);
        setExistingAttachments(comment.attachments || []);
      } else {
        setContent("");
        setExistingAttachments([]);
      }
      setAttachmentsToRemove([]);
      setNewAttachments((prev) => {
        prev.forEach((attachment) => URL.revokeObjectURL(attachment.preview));
        return [];
      });
      setErrors({});
      setSubmitError(null);
    } else {
      setNewAttachments((prev) => {
        prev.forEach((attachment) => URL.revokeObjectURL(attachment.preview));
        return [];
      });
    }
  }, [isOpen, isEditMode, comment]);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!content.trim()) {
      newErrors.content = "Comment content is required";
    } else if (content.trim().length < 1) {
      newErrors.content = "Comment content cannot be empty";
    } else if (content.length > 5000) {
      newErrors.content = "Comment content cannot exceed 5,000 characters";
    }

    const totalAttachments = existingAttachments.length + newAttachments.length;
    if (totalAttachments > MAX_ATTACHMENTS) {
      newErrors.attachments = `You can upload up to ${MAX_ATTACHMENTS} attachments`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const fileList = event.target.files;
    if (!fileList) {
      return;
    }

    const selectedFiles = Array.from(fileList);
    if (selectedFiles.length === 0) {
      return;
    }

    const remainingSlots =
      MAX_ATTACHMENTS - (existingAttachments.length + newAttachments.length);

    if (remainingSlots <= 0) {
      setErrors((prev) => ({
        ...prev,
        attachments: `Maximum ${MAX_ATTACHMENTS} attachments allowed`,
      }));
      event.target.value = "";
      return;
    }

    const filesToProcess = selectedFiles.slice(0, remainingSlots);
    const validationErrors: string[] = [];
    const validFiles: File[] = [];

    // Validate all files first
    for (const file of filesToProcess) {
      const result = validateFile(file);
      if (result.valid) {
        validFiles.push(file);
      } else if (result.error) {
        validationErrors.push(result.error);
      }
    }

    // Warn if some files were skipped due to slot limit
    if (filesToProcess.length < selectedFiles.length) {
      const skipped = selectedFiles.length - filesToProcess.length;
      validationErrors.push(
        `${skipped} file(s) skipped — maximum ${MAX_ATTACHMENTS} attachments allowed`
      );
    }

    // Process valid files (compress if needed)
    const pendingAttachments: PendingAttachment[] = [];
    for (const file of validFiles) {
      const processedFile = await compressImageIfNeeded(file);
      if (processedFile.size > MAX_ATTACHMENT_SIZE_BYTES) {
        validationErrors.push(
          `"${file.name}" — still too large after compression (${(
            processedFile.size /
            (1024 * 1024)
          ).toFixed(1)} MB)`
        );
        continue;
      }
      pendingAttachments.push({
        file: processedFile,
        preview: URL.createObjectURL(processedFile),
      });
    }

    // Add valid attachments
    if (pendingAttachments.length > 0) {
      setNewAttachments((prev) => [...prev, ...pendingAttachments]);
    }

    setErrors((prev) => ({
      ...prev,
      attachments:
        validationErrors.length > 0 ? validationErrors.join("\n") : "",
    }));

    event.target.value = "";
  };

  const handleRemoveExistingAttachment = (attachmentId: number) => {
    if (loading) {
      return;
    }

    setExistingAttachments((prev) =>
      prev.filter((attachment) => attachment.id !== attachmentId)
    );
    setAttachmentsToRemove((prev) =>
      prev.includes(attachmentId) ? prev : [...prev, attachmentId]
    );
    setErrors((prev) => ({
      ...prev,
      attachments: "",
    }));
  };

  const handleRemoveNewAttachment = (index: number) => {
    if (loading) {
      return;
    }

    setNewAttachments((prev) => {
      if (index < 0 || index >= prev.length) {
        return prev;
      }

      const updated = [...prev];
      const [removed] = updated.splice(index, 1);

      if (removed) {
        URL.revokeObjectURL(removed.preview);
      }

      return updated;
    });

    setErrors((prev) => ({
      ...prev,
      attachments: "",
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const trimmedContent = content.trim();
    const attachmentsToAdd = newAttachments.map(
      (attachment) => attachment.file
    );
    const uniqueAttachmentsToRemove = Array.from(new Set(attachmentsToRemove));

    const data: CreateCommentInput | UpdateCommentInput = isEditMode
      ? {
          content: trimmedContent,
          attachmentsToAdd,
          attachmentsToRemove: uniqueAttachmentsToRemove,
        }
      : {
          content: trimmedContent,
          attachments: attachmentsToAdd,
        };

    try {
      setSubmitError(null);
      await onSubmit(data);
    } catch (error) {
      logger.error("Error submitting comment:", error);
      const message =
        (error as any)?.message ||
        "Failed to save the comment. Please try again.";
      setSubmitError(message);
    }
  };

  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setContent(value);

    // Clear content error when user starts typing
    if (errors.content && value.trim()) {
      setErrors((prev) => ({ ...prev, content: "" }));
    }
  };

  if (!isOpen) return null;

  const totalAttachments = existingAttachments.length + newAttachments.length;
  const remainingSlots = Math.max(0, MAX_ATTACHMENTS - totalAttachments);
  const attachmentsDisabled = loading || remainingSlots === 0;

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? "Edit Comment" : "Add Comment"}
      size="lg"
      layout="column"
      closeDisabled={loading}
      message={
        error || submitError
          ? { text: error || submitError, type: "error" }
          : null
      }
      onSubmit={handleSubmit}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" loading={loading} disabled={!content.trim()}>
            {isEditMode ? "Update Comment" : "Add Comment"}
          </Button>
        </>
      }
    >
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-4">
            <div>
              <Label htmlFor="content">Comment *</Label>
              <textarea
                id="content"
                value={content}
                onChange={handleContentChange}
                placeholder="Share your thoughts on this post..."
                rows={4}
                className={`w-full resize-none rounded-md border px-3 py-2 shadow-sm focus:border-biafit-primary focus:outline-none focus:ring-2 focus:ring-biafit-primary dark:border-gray-600 dark:bg-navy-700 dark:text-white dark:placeholder-gray-400 ${
                  errors.content
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                    : "border-gray-300"
                }`}
                disabled={loading}
              />
              {errors.content && (
                <p className="mt-1 text-sm text-red-500">{errors.content}</p>
              )}
              <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {content.length}/5,000 characters
              </div>
            </div>

            <div>
              <Label htmlFor="comment-attachments">Attachments</Label>
              <div className="rounded-md bg-gray-50 p-3 dark:bg-navy-700">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
                  File restrictions:
                </p>
                <ul className="mt-1 list-inside list-disc text-sm text-gray-600 dark:text-gray-300">
                  <li>Allowed types: JPG, PNG, GIF, WEBP</li>
                  <li>Max file size: 10 MB per file</li>
                  <li>Max attachments: {MAX_ATTACHMENTS} files</li>
                </ul>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-3">
                <input
                  id="comment-attachments"
                  type="file"
                  accept=".jpg,.jpeg,.png,.gif,.webp"
                  multiple
                  onChange={handleFileChange}
                  disabled={attachmentsDisabled}
                  className="hidden"
                />
                <label
                  htmlFor="comment-attachments"
                  className={`inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                    attachmentsDisabled
                      ? "cursor-not-allowed bg-gray-200 text-gray-500 dark:bg-gray-700"
                      : "cursor-pointer bg-biafit-primary text-white hover:bg-biafit-secondary"
                  }`}
                >
                  Add Images
                </label>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {totalAttachments}/{MAX_ATTACHMENTS} selected •{" "}
                  {remainingSlots > 0
                    ? `${remainingSlots} attachment${
                        remainingSlots === 1 ? "" : "s"
                      } remaining`
                    : "Maximum attachments reached"}
                </span>
              </div>

              {errors.attachments && (
                <div className="mt-2 rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/30">
                  {errors.attachments.split("\n").map((error, index) => (
                    <p
                      key={index}
                      className="text-sm text-red-600 dark:text-red-400"
                    >
                      {error}
                    </p>
                  ))}
                </div>
              )}

              {(existingAttachments.length > 0 ||
                newAttachments.length > 0) && (
                <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3">
                  {existingAttachments.map((attachment) => (
                    <div
                      key={`existing-${attachment.id}`}
                      className="relative overflow-hidden rounded-md border border-gray-200 dark:border-gray-700"
                    >
                      <img
                        src={attachment.full_path}
                        alt={attachment.original_name || "Comment attachment"}
                        className="h-32 w-full object-cover"
                      />
                      <div className="absolute left-2 top-2">
                        <span className="rounded bg-white/80 px-2 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-900/70 dark:text-gray-200">
                          Existing
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          handleRemoveExistingAttachment(attachment.id)
                        }
                        disabled={loading}
                        className="bg-black/60 hover:bg-black/80 absolute right-2 top-2 rounded-full p-1 text-white transition-colors"
                      >
                        <MdClose className="h-4 w-4" />
                      </button>
                    </div>
                  ))}

                  {newAttachments.map((attachment, index) => (
                    <div
                      key={`new-${index}`}
                      className="relative overflow-hidden rounded-md border border-dashed border-gray-300 bg-gray-50 dark:border-gray-600 dark:bg-gray-800/40"
                    >
                      <img
                        src={attachment.preview}
                        alt={attachment.file.name}
                        className="h-32 w-full object-cover"
                      />
                      <div className="absolute left-2 top-2">
                        <span className="rounded bg-biafit-primary/80 px-2 py-0.5 text-xs font-medium text-white">
                          New
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveNewAttachment(index)}
                        disabled={loading}
                        className="bg-black/60 hover:bg-black/80 absolute right-2 top-2 rounded-full p-1 text-white transition-colors"
                      >
                        <MdClose className="h-4 w-4" />
                      </button>
                      <div className="bg-black/50 absolute bottom-0 left-0 right-0">
                        <p className="truncate px-2 py-1 text-xs text-white">
                          {attachment.file.name}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </ModalShell>
  );
};

export default CommentModal;
